<template>
  <div class="blockDeliveryType">
    <DeliveryTypesSkeleton :loading="loading" data-qa="step3-skeleton" />

    <section
      v-if="deliveryTypes.length && !loading.main && !loading.deliveryTypes"
      id="DeliveryTypes"
      :data-type="activeDeliveryType.ID"
      :class="[
        'w-full',
        { 'delivery-types': currentDeliveryType.ID === 'COURIER' },
        deliveryTypesClass,
      ]"
    >
      <DeliveryTypeCards
        :types="deliveryTypes"
        :active-id="currentDeliveryType.ID"
        :country-code="getCountry.code"
        :currency="getCurrency"
        @select-type="(type) => onClickShowServicesList(type.ITEMS, type)"
      />
      <!-- Выбор ПВЗ при "Самовывозе" -->
      <PickpointSection
        v-if="
          currentDeliveryType.ID === 'PICK_POINT' &&
          !pickpointsLoading &&
          !selectedPickpointInfo
        "
        v-model="field_filterPickpoints"
        :show-search-tools="showSearchTools"
        :services="services"
        :multiple-types="deliveryTypes.length > 1"
        :autocomplete-label="getAutocompleteLabel"
        :is-mobile="isMobile"
        :selected-template="selectedPickupsTemplate"
        @search-pickpoints="(v) => emit('searchPickpoints', v)"
        @filter-pickpoints="filterPickpoints"
        @switch-pickpoints-template="
          (t) => {
            selectedPickupsTemplate = t
            emit('switchPickpointsTemplate', t)
          }
        "
      />
      <CourierServicesSection
        v-if="
          ['COURIER'].includes(currentDeliveryType.ID) &&
          (!showSearchTools ||
            (showSearchTools && currentDeliveryType.ID === 'COURIER'))
        "
        :services="sortedServices"
        :selected-id="serviceId"
        :show-de-notify="getCountry.code === 'DE'"
        @change="changeService"
      />
    </section>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, toRef } from 'vue'
import { useStore } from 'vuex'
import { trackDeliveryMethodSelected } from '@/utils/checkoutAnalytics'
import DeliveryTypesSkeleton from './components/DeliveryTypesSkeleton.vue'
import DeliveryTypeCards from './components/DeliveryTypeCards.vue'
import PickpointSection from './components/PickpointSection.vue'
import CourierServicesSection from './components/CourierServicesSection.vue'
import useActiveDeliveryType from './composables/useActiveDeliveryType.js'
import usePickpointSearch from './composables/usePickpointSearch.js'

const props = defineProps({
  loading: { type: Object, required: true },
  deliveryTypes: { type: Array, required: true },
  positionData: { type: Object, required: true },
  typesFilter: { type: Array, required: true },
  showSearchTools: { type: Boolean, required: true },
  activeDeliveryType: { type: Object, required: true },
  selectedDeliveryService: { type: String, required: true },
  isSelectedProfile: { type: Boolean, default: false },
  lastServiceId: { type: String, default: '' },
})

const emit = defineEmits([
  'selectDeliveryType',
  'callback',
  'filterPickpointsByField',
  'filterPickpointsByType',
  'passMapCords',
  'switchPickpointsTemplate',
  'searchPickpoints',
  'updateDeliveryType',
])

const store = useStore()
const { proxy } = getCurrentInstance()

const activeTooltip = ref(undefined)

const { currentDeliveryType, pickpointsLoading } = useActiveDeliveryType({
  activeDeliveryType: toRef(props, 'activeDeliveryType'),
  typesFilter: toRef(props, 'typesFilter'),
  deliveryTypes: toRef(props, 'deliveryTypes'),
  showServicesList,
  resetService: () => {
    serviceId.value = ''
  },
})

const { field_filterPickpoints, filterPickpoints } = usePickpointSearch({
  emit,
})

const selectedPickupsTemplate = ref('list')

const serviceState = computed(() => store.state.serviceState)

const services = computed(() => {
  if (
    props.typesFilter.length > 0 &&
    currentDeliveryType.value.ID === 'PICK_POINT'
  ) {
    return currentDeliveryType.value.ITEMS.filter((e) =>
      props.typesFilter.includes(e.ID)
    )
  }
  return currentDeliveryType.value.ITEMS
})

const getCountry = computed(() => store.state.country)
const getCurrency = computed(() => store.state.currency)

const selectedPickpointInfo = computed(
  () => store.getters.getSelectedPickpointInfo
)
const countriesDeliveryPriority = computed(
  () => proxy.$checkoutOptions?.COUNTRIES_DELIVERY_PRIORITY
)

const sortedServices = computed(() => {
  const objs = JSON.parse(JSON.stringify(services.value))
  if (countriesDeliveryPriority.value.includes(getCountry.value.code)) {
    // сортировка в файлах логиста Игоря Мялькина, принимаем их и выводим
    return objs
  }
  objs.sort((a, b) => (a.PRICE > b.PRICE ? 1 : b.PRICE > a.PRICE ? -1 : 0))
  return objs
})

const getAutocompleteLabel = computed(() => proxy.$t('delivery_address_label'))
const isMobile = computed(() => proxy.$isMobile)

const serviceId = computed({
  get: () => props.selectedDeliveryService ?? '',
  set: (id) => selectAndEmitService(id),
})

const deliveryTypesClass = computed(() => ({
  loading: props.loading.deliveryTypes,
  'pb-7 mb-7 max-sm:mb-0': props.activeDeliveryType.ID === 'COURIER',
  'pb-6 lg1024:pb-3':
    props.activeDeliveryType.ID !== 'COURIER' &&
    props.activeDeliveryType.ID !== 'PICK_POINT',
}))

function toggleServiceState(value) {
  store.commit('toggleServiceState', value)
}

function changeService(e) {
  serviceId.value = e
  toggleServiceState(!serviceState.value)
}

function editScenario() {
  store.commit('setCheckoutScenario', 'userEditProfile')
}

function onClickShowServicesList(svcList, deliveryType) {
  if (currentDeliveryType.value?.ID !== deliveryType.ID) {
    trackDeliveryMethodSelected({
      source: 'manual_input',
      trigger: 'user_action',
      method: deliveryType.ID,
    })
  }
  showServicesList(svcList, deliveryType)
  editScenario()
  emit('updateDeliveryType', deliveryType)
  toggleServiceState(!serviceState.value)
}

function showServicesList(svcList, deliveryType) {
  store.commit('UpdateSelectedDeliveryType', deliveryType)
  store.commit('UpdateDeliveryCompaniesList', svcList)
  // Используем lastServiceId из профиля только если он реально присутствует
  // в списке сервисов для текущего города. Иначе профильный сервис
  // (например, I_SDEK_D из Грозного) затирает корректный выбор
  // _setupActiveDelivery (I_COURIER_BALANCE для Москвы).
  const lastServiceExists =
    props.lastServiceId &&
    svcList?.some?.((item) => item.ID === props.lastServiceId)

  const sId =
    props.isSelectedProfile && lastServiceExists
      ? props.lastServiceId
      : svcList?.[0]?.ID
  store.commit('UpdateSelectedDeliveryTypeService', sId)

  emit('selectDeliveryType', deliveryType)
  // пре-селектнутая служба обязана пересчитать оплаты через единый callback-канал; guard гасит повтор при уже активной службе
  if (sId && sId !== props.selectedDeliveryService) {
    serviceId.value = sId
  }
  if (activeTooltip.value !== deliveryType.ID) {
    activeTooltip.value = deliveryType.ID
  } else {
    activeTooltip.value = undefined
  }
}

function selectAndEmitService(id) {
  emit('callback', id)
}
</script>

<style lang="scss" scoped>
.hidden {
  display: none !important;
}
.disabled-selection {
  display: none;
}

.delivery-types {
  border-bottom: 1px solid #e5ecf1;

  @media (max-width: 640px) {
    border-bottom: none;
  }
}
</style>
